/**
 * Sample app definitions for demonstration
 * These would normally be loaded from the app marketplace/registry
 */

import { App } from '../types/apps';
import { v4 as uuidv4 } from 'uuid';

export const sampleApps: App[] = [
  {
    id: 'credit-score-field-app',
    name: 'Credit Score Field',
    description: 'A specialized form field for entering and validating credit scores with industry standards',
    version: '1.0.0',
    author: {
      name: 'iBusiness Team',
      email: 'apps@ibusiness.ai',
      url: 'https://ibusiness.ai'
    },
    category: 'foundational',
    isActive: true,
    isPublic: true,
    manifest: {
      entry: 'dist/index.js',
      permissions: ['read-forms', 'write-forms'],
      hooks: [
        { name: 'form-field-provider', id: uuidv4(), function: 'registerCreditScoreField' }
      ],
      configuration: {
        minScore: 300,
        maxScore: 850,
        bureaus: ['Experian', 'Equifax', 'TransUnion']
      }
    },
    pricing: {
      model: 'free'
    },
    screenshots: [
      'https://example.com/screenshots/credit-score-field-1.png'
    ],
    documentation: {
      readme: '# Credit Score Field App\n\nProvides industry-standard credit score input and validation.',
      website: 'https://ibusiness.ai/apps/credit-score-field'
    },
    ratings: {
      average: 4.8,
      count: 156
    },
    downloads: 1250,
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-01-20T14:30:00Z',
    publishedAt: '2024-01-16T09:00:00Z',
    createdBy: 'ibusiness-team'
  },
  {
    id: 'bank-verification-app',
    name: 'Bank Account Verification',
    description: 'Automatically verify bank account information using third-party services',
    version: '2.1.0',
    author: {
      name: 'FinTech Solutions',
      email: 'support@fintechsolutions.com',
      url: 'https://fintechsolutions.com'
    },
    category: 'foundational',
    isActive: true,
    isPublic: true,
    manifest: {
      entry: 'dist/index.js',
      permissions: ['external-api', 'read-forms'],
      hooks: [
        { name: 'validation-provider', id: uuidv4(), function: 'validateBankAccount' },
        { name: 'form-submitted', id: uuidv4(), function: 'onFormSubmitted' }
      ]
    },
    pricing: {
      model: 'usage-based',
      price: 0.25,
      currency: 'USD'
    },
    compatibility: {
      minVersion: '1.0.0'
    },
    documentation: {
      readme: '# Bank Account Verification App\n\nReal-time bank account validation.',
      website: 'https://fintechsolutions.com/bank-verification'
    },
    ratings: {
      average: 4.6,
      count: 89
    },
    downloads: 567,
    createdAt: '2024-02-01T08:30:00Z',
    updatedAt: '2024-02-15T16:45:00Z',
    publishedAt: '2024-02-02T10:00:00Z',
    createdBy: 'fintech-solutions'
  },
  {
    id: 'risk-assessment-action',
    name: 'AI Risk Assessment',
    description: 'Advanced AI-powered risk assessment for loan applications',
    version: '1.5.2',
    author: {
      name: 'RiskTech AI',
      email: 'info@risktech.ai',
      url: 'https://risktech.ai'
    },
    category: 'loan-processing',
    isActive: true,
    isPublic: true,
    manifest: {
      entry: 'dist/index.js',
      permissions: ['external-api', 'read-clients', 'read-loans'],
      hooks: [
        { name: 'rule-action-provider', id: uuidv4(), function: 'executeRiskAssessment' }
      ]
    },
    pricing: {
      model: 'subscription',
      price: 99,
      currency: 'USD',
      billingCycle: 'monthly'
    },
    documentation: {
      readme: '# AI Risk Assessment App\n\nML-powered risk scoring for loan decisions.',
      website: 'https://risktech.ai/risk-assessment'
    },
    ratings: {
      average: 4.9,
      count: 234
    },
    downloads: 2100,
    createdAt: '2024-01-10T12:00:00Z',
    updatedAt: '2024-02-20T11:15:00Z',
    publishedAt: '2024-01-12T14:00:00Z',
    createdBy: 'risktech-ai'
  },
  {
    id: 'docusign-integration',
    name: 'DocuSign Integration',
    description: 'Seamlessly integrate document signing workflows with DocuSign',
    version: '3.0.1',
    author: {
      name: 'DocuSign Partners',
      email: 'partners@docusign.com',
      url: 'https://docusign.com'
    },
    category: 'workflow',
    isActive: true,
    isPublic: true,
    manifest: {
      entry: 'dist/index.js',
      permissions: ['external-api', 'read-loans', 'write-loans'],
      hooks: [
        { name: 'workflow-step-provider', id: uuidv4(), function: 'createSigningEnvelope' }
      ]
    },
    pricing: {
      model: 'usage-based',
      price: 2.50,
      currency: 'USD'
    },
    screenshots: [
      'https://example.com/screenshots/docusign-1.png',
      'https://example.com/screenshots/docusign-2.png'
    ],
    documentation: {
      readme: '# DocuSign Integration App\n\nPowerful document signing integration.',
      website: 'https://docusign.com/partners/ibusiness'
    },
    ratings: {
      average: 4.7,
      count: 345
    },
    downloads: 5400,
    createdAt: '2023-12-01T09:00:00Z',
    updatedAt: '2024-02-18T13:20:00Z',
    publishedAt: '2023-12-03T11:00:00Z',
    createdBy: 'docusign-partners'
  },
  {
    id: 'salesforce-sync',
    name: 'Salesforce CRM Sync',
    description: 'Bi-directional synchronization with Salesforce CRM systems',
    version: '2.3.0',
    author: {
      name: 'Integration Experts',
      email: 'hello@integrationexperts.com'
    },
    category: 'integration',
    isActive: true,
    isPublic: true,
    manifest: {
      entry: 'dist/index.js',
      permissions: ['external-api', 'read-clients', 'write-clients', 'read-loans'],
      hooks: [
        { name: 'client-created', id: uuidv4(), function: 'syncClientToSalesforce' },
        { name: 'loan-created', id: uuidv4(), function: 'syncLoanToSalesforce' }
      ]
    },
    pricing: {
      model: 'subscription',
      price: 149,
      currency: 'USD',
      billingCycle: 'monthly'
    },
    documentation: {
      readme: '# Salesforce CRM Sync App\n\nKeep your CRM data in perfect sync.',
      website: 'https://integrationexperts.com/salesforce-sync'
    },
    ratings: {
      average: 4.5,
      count: 178
    },
    downloads: 890,
    createdAt: '2024-01-25T14:00:00Z',
    updatedAt: '2024-02-22T10:30:00Z',
    publishedAt: '2024-01-27T16:00:00Z',
    createdBy: 'integration-experts'
  },
  {
    id: 'compliance-reporter',
    name: 'Compliance Reporting Suite',
    description: 'Automated regulatory compliance reporting for financial institutions',
    version: '1.8.0',
    author: {
      name: 'RegTech Pro',
      email: 'compliance@regtech.pro',
      url: 'https://regtech.pro'
    },
    category: 'utility',
    isActive: true,
    isPublic: true,
    manifest: {
      entry: 'dist/index.js',
      permissions: ['read-loans', 'read-clients', 'external-api'],
      hooks: []
    },
    pricing: {
      model: 'subscription',
      price: 299,
      currency: 'USD',
      billingCycle: 'monthly'
    },
    documentation: {
      readme: '# Compliance Reporting Suite\n\nStay compliant with automated reporting.',
      website: 'https://regtech.pro/compliance-suite'
    },
    ratings: {
      average: 4.8,
      count: 67
    },
    downloads: 423,
    createdAt: '2024-02-05T11:00:00Z',
    updatedAt: '2024-02-25T15:45:00Z',
    publishedAt: '2024-02-07T13:00:00Z',
    createdBy: 'regtech-pro'
  },
  {
    id: 'email-plugin',
    name: 'Email Plugin',
    description: 'Comprehensive email functionality with form fields, rule actions, and event tracking for email communications',
    version: '1.0.0',
    author: {
      name: 'iBusiness Team',
      email: 'apps@ibusiness.ai',
      url: 'https://ibusiness.ai'
    },
    category: 'integration',
    isActive: true,
    isPublic: true,
    manifest: {
      entry: 'dist/index.js',
      permissions: ['read-forms', 'write-forms', 'read-rules', 'write-rules', 'external-api', 'read-clients'],
      hooks: [
        { name: 'form-field-provider', id: uuidv4(), function: 'registerEmailField' },
        { name: 'rule-action-provider', id: uuidv4(), function: 'registerEmailActions' },
        { name: 'form-submitted', id: uuidv4(), function: 'onFormSubmitted' },
        { name: 'rule-executed', id: uuidv4(), function: 'onRuleExecuted' },
        { name: 'client-created', id: uuidv4(), function: 'onClientCreated' },
        { name: 'loan-created', id: uuidv4(), function: 'onLoanCreated' },
        { name: 'email-sent', id: uuidv4(), function: 'onEmailSent' },
        { name: 'email-opened', id: uuidv4(), function: 'onEmailOpened' },
        { name: 'email-clicked', id: uuidv4(), function: 'onEmailClicked' },
        { name: 'email-bounced', id: uuidv4(), function: 'onEmailBounced' },
        { name: 'email-failed', id: uuidv4(), function: 'onEmailFailed' }
      ],
      configuration: {
        smtp: {
          host: 'smtp.gmail.com',
          port: 587,
          secure: false,
          auth: {
            user: '',
            pass: ''
          }
        },
        templates: {
          welcome: {
            subject: 'Welcome to {{company.name}}',
            body: 'Hello {{client.name}}, welcome to our lending platform!'
          },
          loanApproved: {
            subject: 'Loan Approved - {{loan.id}}',
            body: 'Congratulations {{client.name}}, your loan application has been approved!'
          },
          loanRejected: {
            subject: 'Loan Application Update - {{loan.id}}',
            body: 'Dear {{client.name}}, we regret to inform you that your loan application requires additional review.'
          },
          documentRequest: {
            subject: 'Documents Required - {{loan.id}}',
            body: 'Dear {{client.name}}, please provide the following documents to complete your application.'
          }
        },
        events: {
          trackOpens: true,
          trackClicks: true,
          retryAttempts: 3
        }
      }
    },
    pricing: {
      model: 'free'
    },
    screenshots: [
      'https://example.com/screenshots/email-plugin-form-field.png',
      'https://example.com/screenshots/email-plugin-rule-action.png',
      'https://example.com/screenshots/email-plugin-templates.png'
    ],
    documentation: {
      readme: `# Email Plugin

A comprehensive email integration plugin that provides form fields, rule actions, and event tracking for email communications.

## Features

### Form Fields
- **Email Input Field**: Validated email input with real-time validation
- **Email List Field**: Multiple email address collection
- **Email Template Selector**: Choose from pre-configured templates

### Rule Actions
- **Send Email**: Send emails to clients, leads, or custom recipients
- **Send Template Email**: Use pre-configured templates with dynamic content
- **Schedule Email**: Schedule emails for later delivery
- **Send Bulk Email**: Send emails to multiple recipients

### Event Tracking
- **Email Sent**: Triggered when an email is successfully sent
- **Email Opened**: Track when recipients open emails
- **Email Clicked**: Track link clicks within emails
- **Email Bounced**: Handle bounced emails
- **Email Failed**: Handle failed delivery attempts

### Templates
- Welcome emails for new clients
- Loan approval/rejection notifications
- Document request emails
- Payment reminders
- Custom templates with variable substitution

## Configuration

Configure SMTP settings and email templates through the plugin configuration interface.

### SMTP Settings
- Host, port, and authentication
- SSL/TLS support
- Multiple provider support (Gmail, Outlook, SendGrid, etc.)

### Template Variables
Use dynamic variables in email templates:
- {{client.name}}, {{client.email}}, {{client.businessName}}
- {{loan.id}}, {{loan.amount}}, {{loan.status}}
- {{company.name}}, {{company.address}}
- {{user.name}}, {{user.email}}

## Usage

1. Install the Email Plugin from the marketplace
2. Configure SMTP settings in plugin configuration
3. Add email fields to forms using the form builder
4. Create email-sending rules using the rule builder
5. Monitor email events through the dashboard`,
      changelog: '# Changelog\n\n## v1.0.0\n- Initial release\n- Email form fields\n- Basic rule actions\n- Event tracking\n- Template system',
      website: 'https://ibusiness.ai/apps/email-plugin'
    },
    ratings: {
      average: 4.9,
      count: 45
    },
    downloads: 892,
    createdAt: '2024-02-28T10:00:00Z',
    updatedAt: '2024-02-28T10:00:00Z',
    publishedAt: '2024-02-28T11:00:00Z',
    createdBy: 'ibusiness-team'
  },
  {
    id: 'file-explorer-app',
    name: 'File Explorer',
    description: 'Advanced file management system with folder organization, file sharing, OCR processing, and AI-powered classification',
    version: '1.0.0',
    author: {
      name: 'iBusiness Team',
      email: 'apps@ibusiness.ai',
      url: 'https://ibusiness.ai'
    },
    category: 'utility',
    isActive: true,
    isPublic: true,
    manifest: {
      entry: 'dist/index.js',
      permissions: ['read-files', 'write-files', 'file-system', 'external-api'],
      hooks: [
        { name: 'page-provider', id: uuidv4(), function: 'registerFileExplorerPage' }
      ],
      configuration: {
        maxFileSize: 52428800, // 50MB
        allowedFileTypes: [
          'application/pdf',
          'image/jpeg',
          'image/png',
          'image/gif',
          'text/plain',
          'text/csv',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ],
        features: {
          folderView: true,
          tabView: true,
          fileSharing: true,
          folderSharing: true,
          ocrProcessing: true,
          fileClassification: true,
          versionControl: true,
          accessControl: true
        },
        ocr: {
          provider: 'aws-textract',
          languages: ['en', 'es', 'fr'],
          autoProcess: false
        },
        classification: {
          provider: 'aws-comprehend',
          categories: [
            'document',
            'financial',
            'legal',
            'identity',
            'tax',
            'bank_statement',
            'invoice',
            'contract'
          ],
          autoClassify: true,
          confidence: 0.8
        }
      }
    },
    pricing: {
      model: 'free'
    },
    screenshots: [
      'https://example.com/screenshots/file-explorer-grid.png',
      'https://example.com/screenshots/file-explorer-list.png',
      'https://example.com/screenshots/file-explorer-upload.png',
      'https://example.com/screenshots/file-explorer-sharing.png'
    ],
    documentation: {
      readme: `# File Explorer App

A comprehensive file management solution for the iBusiness platform with advanced features for document organization, sharing, and processing.

## Core Features

### 1. Folder & Tab View
- **Grid View**: Visual file browsing with thumbnails
- **List View**: Detailed file information in tabular format  
- **Tree View**: Hierarchical folder navigation
- **Tab Navigation**: Switch between Files, Shared, and Recent views

### 2. File Sharing with File System
- **User-based Sharing**: Share files with specific users
- **Role-based Access**: Share with user roles (Admin, Manager, Analyst)
- **Permission Levels**: Read, Write, and Admin permissions
- **Public Links**: Generate shareable public links with expiration

### 3. Folder Sharing
- **Nested Permissions**: Share entire folder hierarchies
- **Inherited Access**: Child files inherit parent folder permissions
- **Bulk Operations**: Apply sharing settings to multiple files

### 4. File OCR Processing
- **Automatic Text Extraction**: Extract text from PDFs and images
- **Multi-language Support**: Process documents in multiple languages
- **Confidence Scoring**: OCR accuracy indicators
- **Manual Processing**: On-demand OCR for specific files

### 5. File Classification
- **AI-powered Categorization**: Automatically classify documents by type
- **Custom Categories**: Support for industry-specific document types
- **Manual Override**: Ability to manually correct classifications
- **Confidence Indicators**: Show classification accuracy

## File Types Supported

- **Documents**: PDF, Word, Text files
- **Images**: JPEG, PNG, GIF
- **Spreadsheets**: Excel, CSV
- **Financial**: Bank statements, tax documents, invoices
- **Legal**: Contracts, agreements, compliance documents

## Security Features

- **Access Control**: Fine-grained permission system
- **Audit Logging**: Track all file operations
- **Version Control**: Maintain file history and versions
- **Encryption**: Files encrypted at rest and in transit

## Integration Points

### Client Management
- Link files to specific clients
- Organize documents by loan applications
- Track document requirements and submissions

### Workflow Integration
- Trigger workflows on file upload
- Automated document processing
- Status tracking and notifications

### Form Builder Integration
- File upload fields in forms
- Document collection workflows
- Required document validation

## Usage Examples

### Setting Up Document Organization
1. Create folder structure (e.g., "Client Documents", "Legal", "Financial")
2. Configure access permissions for different user roles
3. Set up automatic classification rules for incoming documents

### Document Processing Workflow
1. Upload files individually or in bulk
2. Automatic OCR processing extracts text content
3. AI classification categorizes documents
4. Files are organized into appropriate folders
5. Relevant users are notified of new documents

### Sharing and Collaboration
1. Select files or folders to share
2. Choose users or roles to share with
3. Set appropriate permission levels
4. Generate public links for external stakeholders
5. Track sharing activity and access logs

## Configuration Options

### File Upload Settings
- Maximum file size limits
- Allowed file types and extensions
- Virus scanning integration
- Duplicate file handling

### OCR Settings
- Service provider selection (AWS Textract, Google Cloud Vision)
- Language detection and processing
- Automatic vs manual processing
- Quality thresholds

### Classification Settings
- Custom category definitions
- Machine learning model selection
- Confidence thresholds
- Manual review workflows

## Best Practices

### Organization
- Use consistent folder naming conventions
- Implement standardized tagging systems
- Regular cleanup of outdated files
- Maintain proper folder hierarchies

### Security
- Regular access permission reviews
- Monitor sharing activities
- Implement data retention policies
- Regular security audits

### Performance
- Batch upload large numbers of files
- Use appropriate file compression
- Regular system maintenance
- Monitor storage usage

## Technical Requirements

- Modern web browser with HTML5 support
- JavaScript enabled
- Minimum 4GB RAM for large file operations
- Stable internet connection for cloud features`,
      changelog: `# Changelog

## v1.0.0 (2024-03-01)
- Initial release of File Explorer app
- Folder and tab view implementation
- Basic file upload and download
- File sharing with users and roles
- Folder sharing functionality
- OCR processing integration
- AI-powered file classification
- Permission system implementation
- Breadcrumb navigation
- Search and filtering capabilities
- Grid and list view modes
- Context menu operations
- Drag and drop upload support`,
      website: 'https://ibusiness.ai/apps/file-explorer'
    },
    ratings: {
      average: 4.9,
      count: 12
    },
    downloads: 156,
    createdAt: '2024-03-01T09:00:00Z',
    updatedAt: '2024-03-01T09:00:00Z',
    publishedAt: '2024-03-01T10:00:00Z',
    createdBy: 'ibusiness-team'
  },
  {
    id: 'funding-package-app',
    name: 'Funding Package App',
    description: 'Comprehensive funding package management with customer statements, routing information, and automated processing workflows',
    version: '1.0.0',
    author: {
      name: 'iBusiness Team',
      email: 'apps@ibusiness.ai',
      url: 'https://ibusiness.ai'
    },
    category: 'integration',
    isActive: true,
    isPublic: true,
    manifest: {
      entry: 'dist/index.js',
      permissions: [
        'read-forms', 
        'write-forms', 
        'read-rules', 
        'write-rules', 
        'read-workflows', 
        'write-workflows',
        'read-clients', 
        'write-clients',
        'read-loans', 
        'write-loans',
        'external-api'
      ],
      hooks: [
        { name: 'form-field-provider', id: uuidv4(), function: 'registerFundingFields' },
        { name: 'rule-action-provider', id: uuidv4(), function: 'registerFundingActions' },
        { name: 'workflow-step-provider', id: uuidv4(), function: 'registerFundingSteps' },
        { name: 'page-provider', id: uuidv4(), function: 'registerFundingPages' },
        { name: 'validation-provider', id: uuidv4(), function: 'registerFundingValidations' },
        { name: 'loan-created', id: uuidv4(), function: 'onLoanCreated' },
        { name: 'workflow-completed', id: uuidv4(), function: 'onWorkflowCompleted' }
      ],
      configuration: {
        routing: {
          validateRoutingNumbers: true,
          supportedBanks: ['all'],
          achProcessing: {
            enabled: true,
            provider: 'plaid',
            testMode: true
          }
        },
        statements: {
          supportedFormats: ['pdf', 'csv', 'json'],
          autoAnalysis: true,
          retentionDays: 2555, // 7 years
          encryption: true
        },
        funding: {
          maxAmount: 5000000, // $5M
          minAmount: 1000,
          defaultCurrency: 'USD',
          processingTimeHours: 24,
          autoApprovalThreshold: 50000 // $50K
        },
        notifications: {
          enabled: true,
          channels: ['email', 'sms'],
          templates: {
            packageCreated: 'Funding package {{package.id}} has been created',
            fundsDeposited: 'Funds of {{amount}} have been deposited to your account',
            routingVerified: 'Bank routing information has been verified',
            statementProcessed: 'Customer statement analysis completed'
          }
        }
      }
    },
    pricing: {
      model: 'subscription',
      price: 199,
      currency: 'USD',
      billingCycle: 'monthly'
    },
    screenshots: [
      'https://example.com/screenshots/funding-package-form.png',
      'https://example.com/screenshots/routing-validation.png',
      'https://example.com/screenshots/statement-upload.png',
      'https://example.com/screenshots/funding-dashboard.png'
    ],
    documentation: {
      readme: `# Funding Package App

A comprehensive solution for managing funding packages, customer statements, and routing information in loan processing workflows.

## Features

### Form Fields
- **Routing Number Field**: Validates US bank routing numbers with real-time verification
- **Account Number Field**: Secure account number input with masking
- **Bank Selection Field**: Dropdown of supported financial institutions  
- **Statement Upload Field**: Drag-and-drop upload for customer statements
- **Funding Amount Field**: Currency input with validation and limits
- **ACH Authorization Field**: Digital signature capture for ACH authorization

### Rule Actions
- **Validate Routing Info**: Real-time bank routing number and account validation
- **Process Customer Statement**: Automated analysis of uploaded financial statements
- **Calculate Funding Eligibility**: Determine loan amount based on statement analysis
- **Create Funding Package**: Generate comprehensive funding documentation
- **Send ACH Transfer**: Initiate ACH transfers to customer accounts
- **Generate Funding Report**: Create detailed funding package reports

### Workflow Steps
- **Routing Verification Step**: Verify bank account information
- **Statement Analysis Step**: Process and analyze customer statements
- **Funding Approval Step**: Review and approve funding packages
- **ACH Processing Step**: Handle ACH transfer initiation
- **Package Delivery Step**: Deliver funding package to customer

### Page Components
- **Funding Dashboard**: Overview of all funding packages and their status
- **Routing Management**: Manage customer banking information
- **Statement Analyzer**: Upload and analyze customer statements
- **Package Builder**: Create and customize funding packages

### Validations
- US bank routing number validation (ABA format)
- Account number format validation
- Statement file format and size validation
- Funding amount limits and compliance checks
- ACH authorization completeness validation

## Configuration

### Routing Information
- Support for all US banks and credit unions
- Real-time routing number validation via Federal Reserve database
- Secure storage of banking information with encryption
- ACH processing integration with multiple providers

### Customer Statements
- Support for PDF, CSV, and JSON statement formats
- Automated statement analysis using ML algorithms
- Extract key financial metrics (income, expenses, cash flow)
- Store statements securely with 7-year retention

### Funding Processing
- Configurable funding limits and approval thresholds
- Automated ACH transfer processing
- Real-time funding status updates
- Integration with compliance and audit systems

## Usage

### For Loan Officers
1. Use routing fields in loan application forms
2. Upload customer statements for analysis
3. Review automated funding recommendations
4. Approve and process funding packages

### For Underwriters
1. Review statement analysis results
2. Validate funding eligibility calculations
3. Approve or modify funding amounts
4. Monitor compliance requirements

### For Operations
1. Monitor ACH transfer status
2. Generate funding reports
3. Manage exception cases
4. Track funding performance metrics

## Integration

### Bank Account Verification
- Plaid integration for real-time account verification
- Micro-deposit verification as fallback
- Support for major US financial institutions

### Statement Processing
- OCR for PDF statement extraction
- ML-powered financial analysis
- Integration with accounting software APIs
- Fraud detection algorithms

### ACH Processing
- NACHA-compliant ACH file generation
- Integration with multiple ACH processors
- Real-time status tracking
- Automated retry logic for failed transfers

## Security

- PCI DSS compliant data handling
- End-to-end encryption for sensitive data
- Audit logging for all transactions
- Role-based access controls
- Regular security assessments

## Compliance

- Bank Secrecy Act (BSA) compliance
- Anti-Money Laundering (AML) checks
- Know Your Customer (KYC) verification
- FFIEC regulatory requirements
- Automated compliance reporting`,
      changelog: `# Changelog

## v1.0.0 (2024-03-01)
- Initial release
- Routing number validation
- Customer statement processing
- Funding package creation
- ACH integration
- Compliance features
- Security enhancements`,
      website: 'https://ibusiness.ai/apps/funding-package'
    },
    ratings: {
      average: 4.9,
      count: 127
    },
    downloads: 1834,
    createdAt: '2024-03-01T09:00:00Z',
    updatedAt: '2024-03-01T09:00:00Z',
    publishedAt: '2024-03-01T10:00:00Z',
    createdBy: 'ibusiness-team'
  },
  {
    id: 'task-manager-app',
    name: 'Task Manager',
    description: 'Comprehensive task management application with file attachments, configurable workflows, and interactive checklists for efficient project tracking',
    version: '1.0.0',
    author: {
      name: 'iBusiness Team',
      email: 'apps@ibusiness.ai',
      url: 'https://ibusiness.ai'
    },
    category: 'utility',
    isActive: true,
    isPublic: true,
    manifest: {
      entry: 'dist/task-manager.js',
      permissions: ['read-clients', 'write-clients', 'read-loans', 'write-loans', 'file-system'],
      hooks: [
        { name: 'page-provider', id: uuidv4(), function: 'registerTaskManagerPage' },
        { name: 'page-mount', id: uuidv4(), function: 'onTaskManagerMount' },
        { name: 'page-unmount', id: uuidv4(), function: 'onTaskManagerUnmount' }
      ],
      configuration: {
        defaultStatus: 'todo',
        defaultPriority: 'medium',
        allowedFileTypes: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'png', 'txt'],
        maxFileSize: 10485760,
        enableNotifications: true,
        autoAssignment: false,
        requireApprovalForCompletion: false,
        categories: ['Loan Review', 'Compliance', 'Documentation', 'Analysis', 'Training', 'Research'],
        statusWorkflow: {
          todo: ['in_progress', 'archived'],
          in_progress: ['review', 'completed', 'todo'],
          review: ['completed', 'in_progress'],
          completed: ['archived'],
          archived: ['todo']
        }
      }
    },
    pricing: {
      model: 'free'
    },
    screenshots: [
      'https://example.com/screenshots/task-manager-1.png',
      'https://example.com/screenshots/task-manager-2.png',
      'https://example.com/screenshots/task-manager-3.png'
    ],
    documentation: {
      readme: `# Task Manager App

A comprehensive task management solution designed for loan processing and financial services workflows.

## Features

### 🗂️ Provided File Support
- Upload and attach multiple files to tasks
- Support for common file formats (PDF, Office docs, images)
- Secure file storage with size limits
- File preview and download capabilities

### ⚙️ Task Configuration
- Customizable task statuses and priorities
- Configurable workflow transitions
- Task categories for organization
- Notification settings per task
- Approval workflows for sensitive tasks

### ✅ Task Checklist
- Interactive checklist items for task breakdown
- Progress tracking with completion percentages
- Individual item completion timestamps
- Drag and drop reordering
- Automatic progress calculations

### Additional Features
- Real-time task statistics and dashboards
- Advanced filtering and search capabilities
- Comment system for collaboration
- Due date tracking with overdue alerts
- Time estimation and tracking
- Tag-based organization
- Role-based access control

## Configuration Options

The app supports extensive configuration to match your workflow:

- **Default Status**: Set the initial status for new tasks
- **Default Priority**: Set the default priority level
- **File Upload Settings**: Configure allowed file types and size limits
- **Workflow Rules**: Define status transition rules
- **Categories**: Customize task categories for your organization
- **Notifications**: Enable/disable various notification types

## Integration

This app integrates seamlessly with the iBusiness platform:
- Access to client and loan data for context
- Automatic task creation from loan workflows
- Integration with form submissions and rule actions
- Audit trail for compliance requirements`,
      website: 'https://ibusiness.ai/apps/task-manager',
      changelog: `## v1.0.0 (2024-03-01)

### ✨ Features
- Complete task management interface
- File attachment system (Provided File)
- Configurable task workflows (Task Configuration)
- Interactive checklists (Task Checklist)
- Real-time statistics dashboard
- Advanced search and filtering
- Comment system for collaboration

### 🔧 Configuration
- Customizable task statuses and priorities
- Workflow transition rules
- File upload restrictions
- Category management
- Notification preferences

### 🏗️ Architecture
- Built as a page-type app for iBusiness platform
- Uses React with Material-UI components
- Integrates with platform authentication
- Follows iBusiness design standards`
    },
    ratings: {
      average: 4.9,
      count: 45
    },
    downloads: 234,
    createdAt: '2024-03-01T09:00:00Z',
    updatedAt: '2024-03-01T09:00:00Z',
    publishedAt: '2024-03-01T10:00:00Z',
    createdBy: 'ibusiness-team'
  }
];

// Mock installed apps for demonstration
export const sampleInstalledApps = [
  {
    appId: 'credit-score-field-app',
    version: '1.0.0',
    isEnabled: true,
    installedAt: '2024-02-01T10:00:00Z',
    installedBy: 'current-user',
    configuration: {
      defaultBureau: 'Experian',
      showRange: true
    },
    app: {
      id: 'credit-score-field-app',
      name: 'Credit Score Field',
      description: 'A specialized form field for entering and validating credit scores with industry standards',
      version: '1.0.0',
      author: {
        name: 'iBusiness Team',
        email: 'apps@ibusiness.ai'
      },
      category: 'foundational' as const,
    }
  },
  {
    appId: 'risk-assessment-action',
    version: '1.5.2',
    isEnabled: true,
    installedAt: '2024-02-10T14:30:00Z',
    installedBy: 'current-user',
    configuration: {
      apiKey: '***',
      threshold: 0.75
    },
    app: {
      id: 'risk-assessment-action',
      name: 'AI Risk Assessment',
      description: 'Advanced AI-powered risk assessment for loan applications',
      version: '1.5.2',
      author: {
        name: 'RiskTech AI'
      },
      category: 'loan-processing' as const,
    }
  },
  {
    appId: 'email-plugin',
    version: '1.0.0',
    isEnabled: true,
    installedAt: '2024-02-28T12:00:00Z',
    installedBy: 'current-user',
    configuration: {
      smtp: {
        host: 'smtp.gmail.com',
        port: 587,
        secure: false,
        auth: {
          user: 'demo@ibusiness.ai',
          pass: '***'
        }
      },
      defaultTemplate: 'welcome',
      trackEvents: true
    },
    app: {
      id: 'email-plugin',
      name: 'Email Plugin',
      description: 'Comprehensive email functionality with form fields, rule actions, and event tracking for email communications',
      version: '1.0.0',
      author: {
        name: 'iBusiness Team'
      },
      category: 'integration' as const,
    }
  },
  {
    appId: 'file-explorer-app',
    version: '1.0.0',
    isEnabled: true,
    installedAt: '2024-03-01T11:00:00Z',
    installedBy: 'current-user',
    configuration: {
      maxFileSize: 52428800,
      autoOCR: false,
      autoClassify: true,
      defaultView: 'list'
    },
    app: {
      id: 'file-explorer-app',
      name: 'File Explorer',
      description: 'Advanced file management system with folder organization, file sharing, OCR processing, and AI-powered classification',
      version: '1.0.0',
      author: {
        name: 'iBusiness Team'
      },
      category: 'utility' as const,
    }
  },
  {  
    appId: 'funding-package-app',
    version: '1.0.0',
    isEnabled: true,
    installedAt: '2024-03-01T11:00:00Z',
    installedBy: 'current-user',
    configuration: {
      routing: {
        validateRoutingNumbers: true,
        achProcessing: {
          enabled: true,
          provider: 'plaid',
          testMode: true
        }
      },
      funding: {
        maxAmount: 500000, // $500K for demo
        autoApprovalThreshold: 25000 // $25K
      },
      notifications: {
        enabled: true,
        channels: ['email']
      }
    },
    app: {
      id: 'funding-package-app',
      name: 'Funding Package App',
      description: 'Comprehensive funding package management with customer statements, routing information, and automated processing workflows',
      version: '1.0.0',
      author: {
        name: 'iBusiness Team'
      },
      category: 'integration' as const,
    }
  },
  {
    appId: 'task-manager-app',
    version: '1.0.0',
    isEnabled: true,
    installedAt: '2024-03-01T10:30:00Z',
    installedBy: 'current-user',
    configuration: {
      defaultStatus: 'todo',
      defaultPriority: 'medium',
      allowedFileTypes: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'png', 'txt'],
      maxFileSize: 10485760,
      enableNotifications: true,
      categories: ['Loan Review', 'Compliance', 'Documentation', 'Analysis', 'Training', 'Research']
    },
    app: {
      id: 'task-manager-app',
      name: 'Task Manager',
      description: 'Comprehensive task management application with file attachments, configurable workflows, and interactive checklists',
      version: '1.0.0',
      author: {
        name: 'iBusiness Team'
      },
      category: 'utility' as const,
    }
  }
];
